import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Paper, List, ListItem, Chip, Divider, LinearProgress, Tooltip,
  Collapse, IconButton,
} from '@mui/material';
import { DragIndicator, WarningAmber, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import apiService from '../../services/api';
import { createLogger, adminLogContext } from '../../utils/logger';
import { addUserActionBreadcrumb } from '../../utils/sentryTracking';
import {
  RG_STAGE_COLUMNS,
  buildStageMap,
  formatInt,
  formatUsd,
  formatTokens,
  formatStageShort,
  stageTooltip,
  getReportsSaved,
  getTotalTokens,
} from '../../utils/reportGenStages';
import { StageDetail } from './TaskHistory';

const logger = createLogger('QueueManager');

const STATS_MIN_WIDTH = 360
  + 56 + 64 + 72 + 70
  + RG_STAGE_COLUMNS.reduce((sum, col) => sum + col.width, 0);

const getStatusChipColor = (status) => {
  switch (status) {
    case 'processing':
    case 'running':
      return 'primary';
    case 'queued':
      return 'default';
    case 'failed':
      return 'error';
    case 'completed':
      return 'success';
    default:
      return 'default';
  }
};

const StageCell = ({ stage, width }) => {
  const failed = Number(stage?.items_failed || 0);
  const text = formatStageShort(stage);
  return (
    <Tooltip title={stageTooltip(stage)}>
      <Typography
        variant="body2"
        sx={{
          width,
          textAlign: 'right',
          color: failed > 0 ? 'error.main' : stage?.status === 'running' ? 'primary.main' : 'inherit',
          fontWeight: failed > 0 ? 700 : stage?.status === 'running' ? 600 : 400,
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

const StatsHeader = () => (
  <Box sx={{ display: 'flex', gap: 1, minWidth: STATS_MIN_WIDTH, color: 'text.secondary' }}>
    <Typography variant="caption" sx={{ width: 56, textAlign: 'right', fontWeight: 600 }}>Docs</Typography>
    {RG_STAGE_COLUMNS.map((col) => (
      <Typography key={col.key} variant="caption" sx={{ width: col.width, textAlign: 'right', fontWeight: 600 }}>
        {col.label}
      </Typography>
    ))}
    <Typography variant="caption" sx={{ width: 64, textAlign: 'right', fontWeight: 600 }}>Reports</Typography>
    <Typography variant="caption" sx={{ width: 72, textAlign: 'right', fontWeight: 600 }}>Tokens</Typography>
    <Typography variant="caption" sx={{ width: 70, textAlign: 'right', fontWeight: 600 }}>Cost</Typography>
  </Box>
);

const TaskStatsCells = ({ task }) => {
  const stageMap = useMemo(() => buildStageMap(task.stages), [task.stages]);
  const reportsSaved = getReportsSaved(task, stageMap);
  const totalTokens = getTotalTokens(task);

  return (
    <Box sx={{ display: 'flex', gap: 1, minWidth: STATS_MIN_WIDTH, alignItems: 'center' }}>
      <Typography variant="body2" sx={{ width: 56, textAlign: 'right' }}>
        {formatInt(task.files_to_process || 0)}
      </Typography>
      {RG_STAGE_COLUMNS.map((col) => (
        <StageCell key={col.key} stage={stageMap[col.key]} width={col.width} />
      ))}
      <Typography variant="body2" sx={{ width: 64, textAlign: 'right' }}>
        {formatInt(reportsSaved)}
      </Typography>
      <Tooltip title={`in: ${formatTokens(task.total_input_tokens)} | out: ${formatTokens(task.total_output_tokens)} | think: ${formatTokens(task.total_thinking_tokens)}`}>
        <Typography variant="body2" sx={{ width: 72, textAlign: 'right' }}>
          {formatTokens(totalTokens)}
        </Typography>
      </Tooltip>
      <Typography variant="body2" sx={{ width: 70, textAlign: 'right' }}>
        {formatUsd(task.total_cost)}
      </Typography>
    </Box>
  );
};

const TaskIdentity = ({ task, showHeartbeatWarning = false }) => (
  <Box sx={{ flex: '1 1 360px', minWidth: 240 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {task.county_name || 'Unknown'}
      </Typography>
      <Chip label={task.status} color={getStatusChipColor(task.status)} size="small" />
      {showHeartbeatWarning && task.status === 'running' && task.last_heartbeat_at
        && (Date.now() - new Date(task.last_heartbeat_at).getTime() > 10 * 60 * 1000) && (
        <Tooltip title={`Heartbeat stale since ${new Date(task.last_heartbeat_at).toLocaleString()} — task may have crashed`}>
          <WarningAmber sx={{ fontSize: 16, color: 'warning.main', ml: 0.5 }} />
        </Tooltip>
      )}
    </Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 760 }}
    >
      {task.status_message || `Generated ${getReportsSaved(task, buildStageMap(task.stages))} reports so far`}
    </Typography>
  </Box>
);

function SortableItem({ id, task }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  return (
    <ListItem ref={setNodeRef} style={style}>
      <Box {...attributes} {...listeners} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', pr: 2 }}>
        <DragIndicator />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, overflowX: 'auto' }}>
        <TaskIdentity task={task} />
        <TaskStatsCells task={task} />
      </Box>
    </ListItem>
  );
}

const RunningTaskCard = ({ task, expanded, onToggle }) => {
  const hasStages = (task.stages || []).length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 1,
          gap: 2,
          cursor: hasStages ? 'pointer' : 'default',
        }}
        onClick={() => hasStages && onToggle()}
      >
        {hasStages && (
          <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); onToggle(); }}>
            {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          </IconButton>
        )}
        <LinearProgress variant="determinate" value={task.progress || 0} sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 48, textAlign: 'right' }}>
          {`${task.progress || 0}%`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, overflowX: 'auto', mt: 1 }}>
        <TaskIdentity task={task} showHeartbeatWarning />
        <TaskStatsCells task={task} />
      </Box>
      {hasStages && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1, bgcolor: 'action.hover', borderRadius: 1, px: 1 }}>
            <StageDetail stages={task.stages} />
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};

const QueueManager = ({ tasks = [], onQueueUpdate }) => {
  const safeTasks = useMemo(() => (Array.isArray(tasks) ? tasks : []), [tasks]);
  const [expandedRunningTaskId, setExpandedRunningTaskId] = useState(null);

  const runningTasks = useMemo(
    () => safeTasks.filter((t) => t.status === 'running' || t.status === 'processing'),
    [safeTasks],
  );
  const queuedTasks = useMemo(
    () => safeTasks.filter((t) => t.status === 'queued' || t.status === 'failed'),
    [safeTasks],
  );

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = queuedTasks.findIndex((t) => t.id === active.id);
      const newIndex = queuedTasks.findIndex((t) => t.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reorderedQueued = arrayMove(queuedTasks, oldIndex, newIndex);
      const orderedIds = [...runningTasks.map((t) => t.id), ...reorderedQueued.map((t) => t.id)];

      try {
        addUserActionBreadcrumb('Reordered report generation queue', {
          view: 'admin_reports',
          action: 'reorder_queue',
          task_count: orderedIds.length,
        });
        await apiService.reorderQueue(orderedIds);
      } catch (error) {
        logger.apiFailure('Failed to reorder queue', adminLogContext('file_and_report_management', {
          action: 'reorder_queue',
          task_count: orderedIds.length,
        }), error);
      } finally {
        onQueueUpdate();
      }
    }
  };

  return (
    <Paper sx={{ mb: 2, p: 2, opacity: tasks.length === 0 ? 0.7 : 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" gutterBottom>
          Processing Queue
        </Typography>
      </Box>

      {runningTasks.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {runningTasks.length === 1 ? 'Current Task' : `Running Tasks (${runningTasks.length})`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 0.5, px: 0.5, color: 'text.secondary' }}>
            <Box sx={{ flex: '1 1 360px', minWidth: 240 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>County / Status</Typography>
            </Box>
            <StatsHeader />
          </Box>
          {runningTasks.map((task) => (
            <RunningTaskCard
              key={task.id}
              task={task}
              expanded={expandedRunningTaskId === task.id}
              onToggle={() => setExpandedRunningTaskId((prev) => (prev === task.id ? null : task.id))}
            />
          ))}
        </Box>
      )}

      {queuedTasks.length > 0 && runningTasks.length > 0 && <Divider sx={{ my: 2 }} />}

      {queuedTasks.length > 0 && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Up Next
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 0.5, px: 2, color: 'text.secondary' }}>
            <Box sx={{ flex: '1 1 360px', minWidth: 240 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>County / Status</Typography>
            </Box>
            <StatsHeader />
          </Box>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={queuedTasks.map((t) => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <List dense>
                {queuedTasks.map((task) => (
                  <SortableItem key={task.id} id={task.id} task={task} />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        </Box>
      )}

      {safeTasks.length === 0 && (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          The queue is currently empty.
        </Typography>
      )}
    </Paper>
  );
};

export default QueueManager;
